import { useEffect, type ReactNode } from 'react';
import { Link, useLoaderData } from 'react-router';
import StepBar from '../components/StepBar';
import AccessPass from '../components/welcome/AccessPass';
import CreatorPool from '../components/welcome/CreatorPool';
import NextSteps from '../components/welcome/NextSteps';
import ProfileMeter from '../components/welcome/ProfileMeter';
import TrailerScreen from '../components/welcome/TrailerScreen';
import { useInView } from '../lib/motion';
import { completion } from '../lib/onboarding/progress';
import { ROLES } from '../lib/onboarding/roles';
import { SITE_URL } from '../lib/supabase';
import { TRAILER_ID } from '../lib/trailer';
import type { RootLoaderData } from '../lib/types';
import { INSTAGRAM_URL, markWelcomeSeen, YOUTUBE_URL } from '../lib/welcome';

/**
 * Where induction lands, once: the invitation is under review, here is the
 * trailer, here is what happens next. Marked as seen on arrival — a reload
 * goes to the studio, not back through the ceremony.
 */
export default function Welcome() {
  const { identity, profile } = useLoaderData() as RootLoaderData;

  useEffect(() => {
    markWelcomeSeen(identity.id);
  }, [identity.id]);

  const userType = profile.userType;
  const name = (profile.answers.full_name as string) || identity.name;
  const percent = userType ? completion(userType, profile.answers) : 0;
  const roleLabel = userType ? ROLES[userType].label : 'Creator';

  return (
    <main className="shell welcome">
      <div className="welcome-bg" aria-hidden="true">
        <i />
        <i />
      </div>

      <div className="card card-xl">
        <StepBar left="Early Access · Welcome" right="Invitation under review" />

        <section className="hero">
          <div className="hero-copy">
            <h1 className="d hero-title">
              <span className="hero-line">You&rsquo;re</span>{' '}
              <span className="hero-line">
                in. <Bolt />
              </span>
            </h1>
            <p className="hero-lede">Your Frameon invitation is now under review.</p>
            <p className="sub">
              Welcome to the Frameon Early Access pool. We&rsquo;re opening gradually to creators actively exploring{' '}
              <em>AI filmmaking, advertising, VFX and next-generation production workflows.</em>
            </p>
          </div>
          <AccessPass name={name} memberNo={profile.memberNo} roleLabel={roleLabel} />
        </section>

        <Reveal>
          <TrailerScreen videoId={TRAILER_ID} />
          <p className="mono trailer-cap">Now showing · The Frameon trailer</p>
        </Reveal>

        <Reveal>
          <p className="sub review-note">
            Your profile will now be reviewed based on your experience, how you plan to use Frameon, and how
            completely you&rsquo;ve filled out your creator profile.
          </p>
        </Reveal>

        <div className="rule" />

        <Reveal>
          <p className="mono section-k">What happens next?</p>
        </Reveal>
        <NextSteps />

        <div className="rule" />
        <CreatorPool memberNo={profile.memberNo} />

        <div className="rule" />
        <ProfileMeter percent={percent} />

        <div className="rule" />

        <Reveal>
          <p className="mono section-k">While your invitation is being reviewed…</p>
          <h2 className="d follow-title">Follow the build.</h2>
          <p className="sub">
            Product updates, filmmaking experiments, workflow breakdowns, creator demonstrations and new Early Access
            announcements — across our channels.
          </p>
          <div className="follow">
            <a className="follow-tile" href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer">
              <svg viewBox="0 0 48 48" aria-hidden="true">
                <rect x="6" y="6" width="36" height="36" rx="10" />
                <circle cx="24" cy="24" r="8.5" />
                <circle cx="34.5" cy="13.5" r="1.8" className="solid" />
              </svg>
              <span>
                <span className="mono">Instagram</span>
                <b>Follow on Instagram</b>
              </span>
              <i aria-hidden="true">↗</i>
            </a>
            <a className="follow-tile" href={YOUTUBE_URL} target="_blank" rel="noopener noreferrer">
              <svg viewBox="0 0 48 48" aria-hidden="true">
                <rect x="4" y="10" width="40" height="28" rx="8" />
                <path d="M20 17.5v13l11-6.5z" className="solid" />
              </svg>
              <span>
                <span className="mono">YouTube</span>
                <b>Watch on YouTube</b>
              </span>
              <i aria-hidden="true">↗</i>
            </a>
          </div>
          <p className="mono follow-note">New access waves may also be announced on our social channels.</p>
        </Reveal>

        <div className="rule" />

        <Reveal className="finale">
          <p className="mono section-k">Watch your inbox. Your invitation could be next.</p>
          <h2 className="d finale-title">
            <span>You bring the vision.</span>
            <span>
              Frameon runs the <em>production.</em>
            </span>
          </h2>
          <div className="actions">
            <Link className="btn" to="/">
              Enter the studio →
            </Link>
            <a className="btn ghost" href={SITE_URL}>
              Back to frameon.us
            </a>
          </div>
        </Reveal>
      </div>
    </main>
  );
}

function Reveal({ children, className = '' }: { children: ReactNode; className?: string }) {
  const [ref, seen] = useInView<HTMLDivElement>(0.2);
  return (
    <div ref={ref} className={`reveal${seen ? ' in' : ''}${className ? ` ${className}` : ''}`}>
      {children}
    </div>
  );
}

/** The ⚡, drawn: the stroke traces itself, fills, then throws sparks. */
function Bolt() {
  return (
    <span className="bolt" aria-hidden="true">
      <svg viewBox="0 0 40 60">
        <path d="M24 2 L4 34 H18 L14 58 L36 22 H22 Z" pathLength="100" />
      </svg>
      {Array.from({ length: 10 }, (_, i) => (
        <i key={i} style={{ '--a': `${i * 36}deg` } as React.CSSProperties} />
      ))}
    </span>
  );
}
